// src/utils/appLinks.js
// ===========================================================================
// Links that land EXACTLY where something gets done - used by the
// "How do I…?" guide in the reminders popups.
//
//   Admin Portal   /SupervisorDashboard?go=<tab>        (a main tab)
//                  /SupervisorDashboard?more=<section>  (a ⋯ More screen)
//   Team Dashboard /JobHistory?go=<tab>
//   Home           /?go=<tab>
//   + open=<popup>  opens a popup on arrival (e.g. split-sub-payment)
//
// Pages read go / more with useLinkedTab(); popups listen with
// useOpenOnArrival(name, fn). Other query params (e.g. Expenses' tab=bills)
// are left alone.
// ===========================================================================

import { useEffect } from "react";
import { useLocation, useNavigate } from "react-router-dom";

const OPEN_KEY = "servicing_open_on_arrival";
const OPEN_EVENT = "servicing:open-on-arrival";

export const PAGE_PATHS = {
  admin: "/SupervisorDashboard",
  team: "/JobHistory",
  home: "/",
};

/** "/SupervisorDashboard?go=jobestimatespreadsheet&open=split-sub-payment" */
export const linkTo = ({ page = "admin", path, go, more, open, params = {} } = {}) => {
  const base = path || PAGE_PATHS[page] || "/";
  const q = new URLSearchParams();
  if (go) q.set("go", go);
  if (more) q.set("more", more);
  if (open) q.set("open", open);
  Object.entries(params).forEach(([k, v]) => v != null && q.set(k, v));
  const s = q.toString();
  return s ? `${base}?${s}` : base;
};

/** Navigate there; a popup named by `open` opens when its page is ready. */
export const goTo = (navigate, target = {}) => {
  try {
    if (target.open) sessionStorage.setItem(OPEN_KEY, target.open);
  } catch {
    /* sessionStorage blocked - the open= query param still works */
  }
  navigate(linkTo(target));
  // Already on that page (no remount): nudge listeners.
  setTimeout(() => window.dispatchEvent(new CustomEvent(OPEN_EVENT)), 60);
};

/** The go / more values in the current URL (read once, e.g. in useState). */
export const readLinkParams = () => {
  try {
    const q = new URLSearchParams(window.location.search);
    return { go: q.get("go") || "", more: q.get("more") || "", open: q.get("open") || "" };
  } catch {
    return { go: "", more: "", open: "" };
  }
};

/**
 * Apply ?go= / ?more= whenever the URL changes, then take them out of the
 * address bar (other params stay). apply({ go, more }) returns nothing.
 */
export const useLinkedTab = (apply) => {
  const location = useLocation();
  const navigate = useNavigate();
  useEffect(() => {
    const q = new URLSearchParams(location.search);
    const go = q.get("go") || "";
    const more = q.get("more") || "";
    const open = q.get("open") || "";
    if (!go && !more && !open) return;
    if (open) {
      try {
        sessionStorage.setItem(OPEN_KEY, open);
      } catch {
        /* ignore */
      }
    }
    apply({ go, more });
    q.delete("go");
    q.delete("more");
    q.delete("open");
    const rest = q.toString();
    navigate(`${location.pathname}${rest ? `?${rest}` : ""}`, { replace: true });
    setTimeout(() => window.dispatchEvent(new CustomEvent(OPEN_EVENT)), 60);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [location.search]);
};

/** Run fn once when someone followed a link asking for popup `name`. */
export const useOpenOnArrival = (name, fn) => {
  useEffect(() => {
    const check = () => {
      let want = "";
      try {
        want = sessionStorage.getItem(OPEN_KEY) || "";
      } catch {
        want = "";
      }
      if (want !== name) return;
      try {
        sessionStorage.removeItem(OPEN_KEY);
      } catch {
        /* ignore */
      }
      fn();
    };
    check();
    window.addEventListener(OPEN_EVENT, check);
    return () => window.removeEventListener(OPEN_EVENT, check);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [name]);
};
